/**
 * Будильник перезвонов: когда пикнуть и как красить строку.
 *
 * Чистая часть напоминаний (просьба Evgeniy 30.08.2026): звук и Notification
 * живут в компоненте, здесь — расчёты, которые проверяются тестами. Пороги:
 * до момента — обычная строка, после — «пора» (оранжевый), спустя
 * OVERDUE_AFTER_MS без звонка — «просрочен» (красный).
 */

/** Через сколько «пора» превращается в «просрочен». */
export const OVERDUE_AFTER_MS = 15 * 60_000;

export type DueState = 'future' | 'due' | 'overdue';

export function dueState(dueAt: Date, now: Date): DueState {
  const diff = now.getTime() - dueAt.getTime();
  if (diff < 0) return 'future';
  return diff >= OVERDUE_AFTER_MS ? 'overdue' : 'due';
}

/**
 * Состояние ВЫДАННОЙ карточки перезвона по времени договорённости
 * (lastResult.nextActionAt); null — карточка не перезвон, времени не
 * назначали. Список «Мои контакты» и плашка на карточке красятся тем же
 * правилом, что и блок «Перезвоны»: после выдачи напоминание не гаснет
 * (06.09.2026). undefined допускается: в окне деплоя «новый web + старый api»
 * поля lastResult ещё нет.
 */
export function cardDueState(nextActionAt: string | null | undefined, now: Date): DueState | null {
  if (nextActionAt === null || nextActionAt === undefined) return null;
  const at = new Date(nextActionAt);
  if (Number.isNaN(at.getTime())) return null;
  return dueState(at, now);
}

/**
 * Ближайший момент в будущем, на который надо завести таймер; null — заводить
 * нечего (всё в прошлом или список пуст). Прошедшие моменты таймера не ждут:
 * по ним пульт пикает сразу при загрузке — см. dueNow.
 */
export function nextDueAt(dueTimes: readonly Date[], now: Date): Date | null {
  let next: Date | null = null;
  for (const at of dueTimes) {
    if (at.getTime() <= now.getTime()) continue;
    if (next === null || at.getTime() < next.getTime()) next = at;
  }
  return next;
}

/**
 * Моменты, наступившие с прошлой проверки: по ним пикаем. Ключ — идентификатор
 * перезвона ВМЕСТЕ со временем: по одному номеру договариваются повторно, и
 * новая договорённость обязана пикнуть снова, а та же — не пикать на каждой
 * перерисовке списка (ревью 30.08.2026).
 */
export function newlyDue(
  items: readonly { key: string; dueAt: Date }[],
  alerted: ReadonlySet<string>,
  now: Date,
): string[] {
  return items
    .filter((item) => item.dueAt.getTime() <= now.getTime() && !alerted.has(item.key))
    .map((item) => item.key);
}

/**
 * Задержка таймера до момента. Потолок — int32: setTimeout с задержкой больше
 * ~24.86 суток переполняется и срабатывает НЕМЕДЛЕННО — перезвон через 26 дней
 * закрутил бы перерисовку панели в цикл (ревью 30.08.2026). Обрезанный таймер
 * не теряет писк: сработав раньше, он просто перевзведётся на остаток.
 */
export function timerDelayMs(next: Date, now: Date): number {
  return Math.min(Math.max(250, next.getTime() - now.getTime()), 2 ** 31 - 1);
}
